import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOutletContext, useParams } from 'react-router';
import type { MembershipSummary } from '../../../src/shared/auth';
import {
  LEAD_STAGES,
  LeadStageSchema,
  STAGE_TRANSITIONS,
  type LeadStage,
  type LeadUpdateStageBody,
} from '../../../src/shared/comms';
import { commsKeys, fetchLeads, updateLeadStage } from '../../api/comms';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingScreen } from '../../components/LoadingScreen';
import { useRealtime } from '../../realtime/useRealtime';

function formatTimestamp(epoch: number): string {
  return new Date(epoch * 1000).toLocaleString();
}

const LEAD_STAGE_LABELS: Record<LeadStage, string> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  booked: 'Booked',
  won: 'Won',
  lost: 'Lost',
};

const STAGE_COLORS: Record<LeadStage, string> = {
  new: 'border-slate-300 bg-slate-50',
  contacted: 'border-blue-200 bg-blue-50',
  qualified: 'border-purple-200 bg-purple-50',
  booked: 'border-amber-200 bg-amber-50',
  won: 'border-emerald-200 bg-emerald-50',
  lost: 'border-red-200 bg-red-50',
};

function StageColumn({
  stage,
  leads,
  onLeadClick,
}: {
  stage: LeadStage;
  leads: Array<{
    id: string;
    contactId: string;
    estimatedValueCents: number | null;
    createdAt: number;
  }>;
  onLeadClick: (id: string) => void;
}): React.JSX.Element {
  return (
    <div
      className={`rounded-lg border-2 p-3 ${STAGE_COLORS[stage]}`}
      data-testid={`stage-column-${stage}`}
    >
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-700">
        {LEAD_STAGE_LABELS[stage]}
        <span className="ml-1.5 text-xs font-normal text-slate-400">({leads.length})</span>
      </h3>
      <div className="space-y-2">
        {leads.length === 0 && <p className="py-4 text-center text-xs text-slate-400">No leads</p>}
        {leads.map((lead) => (
          <button
            key={lead.id}
            type="button"
            onClick={() => onLeadClick(lead.id)}
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-left text-sm shadow-sm transition-colors hover:border-slate-300"
            data-testid={`lead-card-${lead.id}`}
          >
            <p className="truncate font-medium text-slate-900">{lead.contactId}</p>
            {lead.estimatedValueCents !== null && (
              <p className="mt-0.5 text-xs text-slate-500">
                ${(lead.estimatedValueCents / 100).toFixed(2)}
              </p>
            )}
            <p className="mt-0.5 text-xs text-slate-400">{formatTimestamp(lead.createdAt)}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function LeadDetailDrawer({
  lead,
  slug,
  onClose,
}: {
  lead: {
    id: string;
    contactId: string;
    stage: LeadStage;
    estimatedValueCents: number | null;
    qualification: Record<string, unknown>;
    sourceCallId: string | null;
    createdAt: number;
  };
  slug: string;
  onClose: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();

  const validTransitions = STAGE_TRANSITIONS[lead.stage] ?? [];

  const stageMutation = useMutation({
    mutationFn: (body: LeadUpdateStageBody) => updateLeadStage(slug, lead.id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: commsKeys.leads.all(slug) });
    },
  });

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
      data-testid="lead-detail"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">{lead.contactId}</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-2.5 py-1 text-xs font-medium text-slate-500 hover:text-slate-900"
          data-testid="lead-detail-close"
        >
          Close
        </button>
      </div>

      <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="text-slate-500">Stage</dt>
        <dd className="capitalize">{lead.stage}</dd>
        {lead.estimatedValueCents !== null && (
          <>
            <dt className="text-slate-500">Est. value</dt>
            <dd>${(lead.estimatedValueCents / 100).toFixed(2)}</dd>
          </>
        )}
        <dt className="text-slate-500">Created</dt>
        <dd>{formatTimestamp(lead.createdAt)}</dd>
      </dl>

      {Object.keys(lead.qualification).length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-medium text-slate-700">Qualification</h3>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            {Object.entries(lead.qualification).map(([key, value]) => (
              <div key={key} className="contents">
                <dt className="text-slate-500">{key}</dt>
                <dd>{String(value)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {lead.sourceCallId !== null && (
        <p className="mt-4 text-xs text-slate-400">Source call: {lead.sourceCallId}</p>
      )}

      {stageMutation.isError && (
        <div className="mt-4">
          <ErrorNotice error={stageMutation.error} />
        </div>
      )}

      <div className="mt-4">
        <h3 className="text-sm font-medium text-slate-700">Change stage</h3>
        <div className="mt-2 flex flex-wrap gap-2">
          {validTransitions.map((target) => (
            <button
              key={target}
              type="button"
              onClick={() => stageMutation.mutate({ stage: target })}
              disabled={stageMutation.isPending}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid={`stage-btn-${target}`}
            >
              {LEAD_STAGE_LABELS[target]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function LeadsPage(): React.JSX.Element {
  const { slug } = useParams<'slug'>();
  const { membership } = useOutletContext<{ membership: MembershipSummary }>();

  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  const leadsQuery = useQuery({
    queryKey: commsKeys.leads.list(slug!),
    queryFn: ({ signal }) => fetchLeads(slug!, {}, signal),
    enabled: slug !== undefined,
  });

  useRealtime(slug!);

  type LeadEntry = NonNullable<typeof leadsQuery.data>['items'][number];

  const leadsByStage: Record<LeadStage, LeadEntry[]> = {
    new: [],
    contacted: [],
    qualified: [],
    booked: [],
    won: [],
    lost: [],
  };

  if (leadsQuery.isSuccess && leadsQuery.data !== undefined) {
    for (const lead of leadsQuery.data.items) {
      const stage = LeadStageSchema.safeParse(lead.stage);
      if (stage.success) {
        leadsByStage[stage.data].push(lead);
      }
    }
  }

  const selectedLead =
    leadsQuery.isSuccess && leadsQuery.data !== undefined
      ? (leadsQuery.data.items.find((l) => l.id === selectedLeadId) ?? null)
      : null;

  if (slug === undefined) {
    return <p className="text-sm text-slate-500">Workspace slug not found.</p>;
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">Leads</h1>
      <p className="mt-1 text-sm text-slate-500">{membership.name}</p>

      {leadsQuery.isError && (
        <div className="mt-4">
          <ErrorNotice error={leadsQuery.error} />
        </div>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="grid grid-cols-3 gap-3 lg:grid-cols-6">
          {leadsQuery.isPending && (
            <div className="col-span-full">
              <LoadingScreen />
            </div>
          )}

          {leadsQuery.isSuccess &&
            LEAD_STAGES.map((stage) => (
              <StageColumn
                key={stage}
                stage={stage}
                leads={leadsByStage[stage]}
                onLeadClick={(id) => setSelectedLeadId(id)}
              />
            ))}
        </div>

        <div>
          {selectedLead !== null ? (
            <LeadDetailDrawer
              lead={selectedLead}
              slug={slug}
              onClose={() => setSelectedLeadId(null)}
            />
          ) : (
            <div className="flex h-full min-h-[200px] items-center justify-center rounded-lg border border-slate-200 bg-white shadow-sm">
              <p className="text-sm text-slate-500">Select a lead to view details.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
